/**
 * 拆分商品(msku 拆分)后，购物车/收藏夹里旧 variant_id 的迁移定位工具。
 *
 * 按“变体属性”（颜色/尺寸等 Option 值）匹配拆分后的新 variant，而不是按旧 variant_id。
 * 原因：两个近似重复商品 A/B 拆分去重时，若某个 SKU 同时存在于 A 和 B，拆分逻辑只会
 * 用其中一方（先处理的那个）的变体去创建新商品，另一方的旧 variant_id 从未被记录到任何
 * 地方——按 variant_id 反查必然查不到。而拆分后每个新商品都会把创建它所用的属性写入
 * product.metafields.custom.attributes（见 msku-migration/split-variants.js buildAttributes），
 * 同一分组内每个成员的属性组合是唯一的，所以只要旧变体的属性组合（如 Color=Red/Size=M）
 * 和某个分组成员的 attributes 一致，就能定位到它，不管这个 SKU 当初来自 A 还是 B。
 *
 * 分组数据来自既有的 product.metafields.custom.group_products（见 snippets/msku-picker.liquid，
 * 每个成员已经带上了自己的 attributes 字段）。这里复用同一份 group 数据抓取方式（fetch 商品页面
 * HTML，解析内嵌的 [data-msku-group-data] JSON，见 assets/msku-picker.js 的 _mskuLoadProductInModal），
 * 只做“定位新 variant”这一件事，具体的购物车/收藏夹迁移操作留给各自的调用方处理。
 */
(function () {
  'use strict';

  if (window.VariantMigration) return;

  // 与 msku-picker.js 共用同一个页面级 HTML 缓存（key = product url）
  window._mskuPageCache = window._mskuPageCache || {};

  var productJsonCache = {};
  var resolveInFlight = {};

  // 同一个失效 variant 可能被多套独立的检测逻辑同时盯上：cart-page-new.js/cart-slide-drawer.js
  // 里通用的"页面加载即检测失效 variant"逻辑，和 fetch-interceptor.js 里 Klip 写入触发的检测，
  // 二者互不知情，几乎同时跑的话会各自 add+remove 一次，导致同一行被迁移两次、数量翻倍。
  // 这里用一个页面生命周期内的内存锁做互斥：谁先对某个旧 variant_id 调用 claimMigration 拿到
  // true，谁就负责实际执行这次迁移；后到的一律拿到 false，直接跳过，交给先到的处理。
  var claimedVariants = {};

  function claimMigration(oldVariantId) {
    var key = String(oldVariantId);
    if (claimedVariants[key]) return false;
    claimedVariants[key] = true;
    return true;
  }

  // claimMigration 只在单次页面生命周期内有效，防不住“整页刷新”这种场景：如果一次迁移
  // 加购新 variant 成功了，但紧接着删除旧行那次请求还没来得及送达就发生了整页刷新（比如
  // 被 Klip 自己的重试逻辑刷新），旧行会原样留在购物车里；刷新后是全新的 JS 上下文，
  // claimedVariants 归零，isVariantValid 再次把这个旧行判定为失效，又会重新加购一次——
  // 数量跟着刷新次数一直往上涨。用 sessionStorage 记一份“这个旧 variant 的数量已经加过”
  // 的标记，跨刷新也认得，加购只做一次，删除可以放心重试多次。
  var ADDED_MARKER_KEY = 'sicotas_migrated_added_variants';

  function readAddedMarkers() {
    try {
      var raw = sessionStorage.getItem(ADDED_MARKER_KEY);
      return new Set(raw ? JSON.parse(raw) : []);
    } catch (e) {
      return new Set();
    }
  }

  function writeAddedMarkers(set) {
    try {
      sessionStorage.setItem(ADDED_MARKER_KEY, JSON.stringify(Array.from(set)));
    } catch (e) {}
  }

  function markVariantsAdded(oldVariantIds) {
    if (!oldVariantIds.length) return;
    var set = readAddedMarkers();
    oldVariantIds.forEach(function (id) { set.add(String(id)); });
    writeAddedMarkers(set);
  }

  function clearAddedMarkers(oldVariantIds) {
    if (!oldVariantIds.length) return;
    var set = readAddedMarkers();
    oldVariantIds.forEach(function (id) { set.delete(String(id)); });
    writeAddedMarkers(set);
  }

  // 每次页面加载（购物车页/全站的购物车抽屉）都会对整个购物车重新扫一遍失效 variant，
  // 哪怕购物车压根没有任何变化（比如从支付页/商品详情页导航回购物车，中间什么都没操作）
  // 也照样重新发一遍 product.js/详情页请求、弹一次迁移遮罩。这里用购物车内容的签名做一层
  // 短路缓存：只要签名跟"上一次确认全部有效、零 anomaly"时记录的签名完全一致，直接跳过
  // 整个扫描；一旦购物车内容有任何变化（加购、改数量、被别的脚本动过、甚至只是某个商品
  // 的 handle 因为新一轮拆分变成了 -original），签名就会变，照常重新扫描——不是"迁移
  // 完成后永久关闭检测"：拆分可能还有下一批，永久关闭会让以后混进购物车的新失效商品
  // 再也没有自动修复的机会。这个缓存只用来短路"页面加载时的主动扫描"（本函数），不影响
  // fetch-interceptor.js 里"每次写请求后对比"的静默清除检测——那是防 Klip App 在跟拆分
  // 无关的日常场景下把商品悄悄清掉的兜底，需要一直保持开启。
  var CLEAN_SIGNATURE_KEY = 'sicotas_cart_migration_clean_signature';

  // 不能把 item.key 算进签名：line item key 会因为折扣重算（典型如 Klip Coupons 每次
  // 写 klipC 属性）、line properties 变化等原因发生轮换，即使 variant_id/quantity 完全
  // 没变。之前把 key 算进去导致签名几乎每次都不一样，"购物车没变就跳过扫描"这层短路
  // 缓存形同虚设——每次打开抽屉/每次加购都要对购物车里每个不同商品重新发一遍
  // /products/{handle}.js 校验请求，实测就是这批多出来的请求量把 Shopify 对该访客的
  // 限流打满，导致后续包括 /cart/add.js 在内的请求接连收到 429。
  // handle 而不是只看 variant_id/quantity：拆分归档后 variant_id 本身不变，只有
  // handle 会从当前商品 slug 变成 {slug}-original，漏看 handle 的话，商品在两次访问
  // 之间被新一轮拆分归档也检测不出来
  function computeCartSignature(cart) {
    var items = (cart && cart.items) || [];
    return items
      .map(function (item) {
        return item.variant_id + ':' + item.quantity + ':' + item.handle;
      })
      .sort()
      .join('|');
  }

  function readCleanSignature() {
    try {
      return sessionStorage.getItem(CLEAN_SIGNATURE_KEY) || '';
    } catch (e) {
      return '';
    }
  }

  function markCartClean(cart) {
    try {
      sessionStorage.setItem(CLEAN_SIGNATURE_KEY, computeCartSignature(cart));
    } catch (e) {}
  }

  function isCartAlreadyClean(cart) {
    var sig = computeCartSignature(cart);
    if (!sig) return true; // 空购物车没什么好扫描的
    return sig === readCleanSignature();
  }

  // 拆分后原商品的 handle 会被归档为 {handle}-original（主商品接管原 handle），
  // 购物车 /cart.js 里的 item.handle 是实时按当前归属商品计算的，拆分后会变成
  // 这个 -original 后缀、且已下架为草稿的旧商品，直接按它查询必然拿不到数据。
  // 命中这个后缀本身就是“已拆分”的强信号，去掉后缀才是新主商品当前实际占用的 handle。
  var ORIGINAL_SUFFIX = /-original$/;

  function stripOriginalSuffix(handle) {
    return ORIGINAL_SUFFIX.test(handle) ? handle.replace(ORIGINAL_SUFFIX, '') : handle;
  }

  // 一次迁移经常要并发查好几个不同商品（isVariantValid + resolveNewVariant 各一轮，
  // 每个商品都要单独请求），量一大就容易把 Shopify/Cloudflare 的限流打到 429——429 是
  // 瞬时的、退避重试几乎总能过去，不该跟"商品真的下架了"一样直接放弃。只重试 429，
  // 其它状态码（404/500 等）不重试，避免真正的失败被无意义地拖慢。
  function fetchWithRetry(url, options, maxAttempts) {
    maxAttempts = maxAttempts || 3;
    function attempt(n) {
      return fetch(url, options).then(function (r) {
        if (r.status === 429 && n < maxAttempts) {
          var delayMs = 500 * Math.pow(2, n - 1); // 500ms, 1000ms
          return new Promise(function (resolve) { setTimeout(resolve, delayMs); })
            .then(function () { return attempt(n + 1); });
        }
        return r;
      });
    }
    return attempt(1);
  }

  // 同一批迁移里可能要并发查好几个不同商品，不加节流的话请求量跟商品数成正比，
  // 商品一多（比如一次 6 个）很容易把接口打到 429（见 fetchWithRetry 的注释）。
  // 这里把"总共同时挂几个请求"限制住，而不是任由 Promise.all 一次性全部发出去。
  function mapWithConcurrency(items, limit, fn) {
    return new Promise(function (resolve) {
      var results = new Array(items.length);
      if (!items.length) return resolve(results);
      var nextIndex = 0;
      var completed = 0;
      function runNext() {
        if (nextIndex >= items.length) return;
        var i = nextIndex++;
        Promise.resolve(fn(items[i], i))
          .then(function (r) { results[i] = r; })
          .catch(function () { results[i] = undefined; })
          .then(function () {
            completed++;
            if (completed === items.length) resolve(results);
            else runNext();
          });
      }
      var initial = Math.min(limit, items.length);
      for (var k = 0; k < initial; k++) runNext();
    });
  }
  var FETCH_CONCURRENCY = 3;

  function fetchProductPageHtml(url) {
    var cached = window._mskuPageCache[url];
    if (typeof cached === 'string') return Promise.resolve(cached);
    if (cached && typeof cached.then === 'function') return cached;

    var promise = fetchWithRetry(url)
      .then(function (r) {
        if (!r.ok) throw new Error('bad status ' + r.status);
        return r.text();
      })
      .then(function (text) {
        window._mskuPageCache[url] = text;
        return text;
      })
      .catch(function (e) {
        delete window._mskuPageCache[url];
        console.error('[variant-migration] failed to fetch product page:', url, e);
        return null;
      });

    window._mskuPageCache[url] = promise;
    return promise;
  }

  function fetchProductJson(handle) {
    if (productJsonCache[handle]) return productJsonCache[handle];

    var promise = fetchWithRetry('/products/' + handle + '.js')
      .then(function (r) {
        // 404 是 Shopify 明确给出的"这个 handle 现在查不到任何商品"，跟超时/断网这类
        // 真正不确定的失败不是一回事——不能一并归为 unknown。旧的近似重复父商品如果
        // 拆分时是直接删除、没有按约定重命名成 {handle}-original 归档保留，它的旧变体
        // 校验就会一直走到这里；如果跟网络失败一样归为 unknown，会被下面 getVariantValidity
        // 保守地当成"仍然有效"，永远进不了迁移流程，那一份数量永远并不到新行上
        if (r.status === 404) return { notFound: true };
        if (!r.ok) throw new Error('bad status ' + r.status);
        return r.json();
      })
      .catch(function (e) {
        delete productJsonCache[handle];
        console.error('[variant-migration] failed to fetch product json:', handle, e);
        return null;
      });

    productJsonCache[handle] = promise;
    return promise;
  }

  /**
   * 商品当前是否仍拥有该 variantId：返回 valid / invalid / unknown。
   * 404（handle 确定不存在）判定为 invalid；其它拉取失败（网络问题等，真正无法判断）
   * 返回 unknown；handle 带 -original 后缀时直接判定为 invalid。
   */
  async function getVariantValidity(handle, variantId) {
    if (ORIGINAL_SUFFIX.test(handle)) return 'invalid';
    var product = await fetchProductJson(handle);
    if (product && product.notFound) return 'invalid';
    if (!product || !product.variants) return 'unknown';
    return product.variants.some(function (v) {
      return String(v.id) === String(variantId);
    }) ? 'valid' : 'invalid';
  }

  // 页面加载主动扫描沿用原来的保守策略：网络状态未知时不把正常商品误判成失效。
  async function isVariantValid(handle, variantId) {
    return (await getVariantValidity(handle, variantId)) !== 'invalid';
  }

  // Quantity 之外的普通 Option 值只做去空格比较；键名/值都要求完全一致（分组内属性组合本就唯一）
  function normalizeAttrValue(v) {
    return typeof v === 'string' ? v.trim() : v;
  }

  function attrsMatch(a, b) {
    var keysA = Object.keys(a || {}).sort();
    var keysB = Object.keys(b || {}).sort();
    if (keysA.length !== keysB.length) return false;
    return keysA.every(function (k, i) {
      return k === keysB[i] && normalizeAttrValue(a[k]) === normalizeAttrValue(b[k]);
    });
  }

  // 购物车行项目的 options_with_values → { OptionName: value } 形式，与 custom.attributes metafield 同构
  function buildItemAttrs(item) {
    var result = {};
    var opts = (item && item.options_with_values) || [];
    opts.forEach(function (opt) {
      if (!opt || !opt.name || opt.value === 'Default Title') return;
      result[opt.name] = opt.value;
    });
    return result;
  }

  /**
   * 用旧变体的属性组合（颜色/尺寸等）反查拆分后的新 variant。
   * item 需要是购物车行项目（或结构等价对象），至少带 options_with_values / variant_id。
   * 返回 { productId, productHandle, productUrl, variantId } 或 null（找不到映射数据/找不到匹配）。
   */
  async function resolveNewVariant(handle, item) {
    var lookupHandle = stripOriginalSuffix(handle);
    var oldVariantId = item && item.variant_id;
    var key = lookupHandle + ':' + oldVariantId;
    if (resolveInFlight[key]) return resolveInFlight[key];

    var task = (async function () {
      var html = await fetchProductPageHtml('/products/' + lookupHandle);
      if (!html) return null;

      var doc = new DOMParser().parseFromString(html, 'text/html');
      var dataEl = doc.querySelector('[data-msku-group-data]');
      if (!dataEl) return null;

      var groupData;
      try {
        groupData = JSON.parse(dataEl.textContent);
      } catch (e) {
        return null;
      }

      var oldAttrs = buildItemAttrs(item);
      var groupProducts = groupData.groupProducts || [];
      for (var i = 0; i < groupProducts.length; i++) {
        var member = groupProducts[i];
        if (!member.attributes) continue;
        if (attrsMatch(member.attributes, oldAttrs)) {
          // member.variantId 来自 snippets/msku-picker.liquid 的 `p.variants.first.id`，
          // 该分组成员商品若此刻没有可用 variant（被后台重新拆分覆盖、临时下架成 draft
          // 等），Liquid 会渲染出空值，落到 JS 这里就是 undefined/NaN。这种坏数据如果
          // 原样放进 /cart/add.js 的 items 数组，JSON.stringify 会把 NaN 静默转成
          // null，Shopify 用 "Cannot find variant" 拒绝整个请求——而 /cart/add.js
          // 对 items 数组是整批原子成功/失败的，一个坏 id 会连带拖垮同批次里其它本来
          // 能正常迁移的商品。这里提前校验，无效就当"未找到映射"处理，让这一行单独
          // 走 anomaly 分支，不影响同批次其它有效行
          var vid = Number(member.variantId);
          if (!vid || !isFinite(vid)) {
            console.error('[variant-migration] group member has invalid variantId, skipping:', member);
            return null;
          }
          return {
            productId: member.id,
            productHandle: member.handle,
            productUrl: member.url,
            variantId: vid,
            // 这个新 variant 当初是由哪个旧 variant 生成的（variant 级 metafield
            // custom.source_variant_id，见 snippets/msku-picker.liquid 里的
            // sourceVariantId 字段）。两个近似重复商品去重收敛到同一个新 variant 时，
            // 用它精确判断"该延续哪一边的优惠券"，而不是瞎猜
            sourceVariantId: member.sourceVariantId || null
          };
        }
      }
      return null;
    })();

    resolveInFlight[key] = task;
    try {
      return await task;
    } finally {
      delete resolveInFlight[key];
    }
  }

  /**
   * 检测购物车里因商品拆分而失效的 variant，并批量迁移成新 variant + 搬运优惠券。
   * 与旧版逐个 add/remove 循环相比，同一批失效商品只发两次请求：
   * 一次 /cart/add.js（items 数组批量加购全部新 variant）+ 一次 /cart/update.js
   * （updates 批量删旧行、attributes 顺带写优惠券搬运结果，二者在同一个请求里原子完成）。
   * 这么做是为了尽量缩短"新商品已经在购物车但优惠券还没写上"这个中间态的时间窗口——
   * Klip 自己的前端逻辑如果在这个窗口读到购物车，会因为算不出折扣而触发它自己的重试/刷新。
   * 返回 { migratedCount, anomalies, migrations }，anomalies 是找不到新 variant 映射
   * 或本轮加购整体失败的旧行项目，migrations 是成功迁移的 { oldVariantId, oldProductId,
   * newVariantId, newProductId } 列表，供调用方渲染 UI 用。
   *
   * options.onStaleDetected(staleItems)：检测阶段一确认购物车里确实有失效商品就立刻调用
   * （早于实际加购/删除），方便调用方只在真的要迁移时才显示加载遮罩，不会让正常购物车
   * 也跟着闪一下遮罩。
   */
  async function migrateCart(cart, options) {
    const empty = { migratedCount: 0, anomalies: [], migrations: [] };
    if (!cart || !cart.items || !cart.items.length) return empty;

    if (isCartAlreadyClean(cart)) {
      console.log('[variant-migration] cart signature unchanged since last clean scan, skipping');
      return empty;
    }

    const itemsByHandle = {};
    cart.items.forEach(function (item) {
      if (!itemsByHandle[item.handle]) itemsByHandle[item.handle] = [];
      itemsByHandle[item.handle].push(item);
    });

    // 限量并发（而不是 Promise.all 一次性全发出去）：商品一多，未节流的并发请求量
    // 很容易把接口打到 429，见 fetchWithRetry 的注释
    const staleItems = [];
    await mapWithConcurrency(Object.keys(itemsByHandle), FETCH_CONCURRENCY, function (handle) {
      return Promise.all(itemsByHandle[handle].map(async function (item) {
        const valid = await isVariantValid(handle, item.variant_id);
        if (!valid) staleItems.push(item);
      }));
    });
    if (!staleItems.length) {
      markCartClean(cart);
      return empty;
    }

    if (options && typeof options.onStaleDetected === 'function') {
      options.onStaleDetected(staleItems);
    }

    // 谁先声明到某个失效 variant_id 就归谁处理，避免跟 fetch-interceptor.js 里
    // Klip 写入触发的检测同时跑，两边各自 add+remove 一次导致数量翻倍
    const claimed = staleItems.filter(function (item) { return claimMigration(item.variant_id); });
    if (!claimed.length) return empty;

    const resolvedList = await mapWithConcurrency(claimed, FETCH_CONCURRENCY, async function (item) {
      return { item: item, resolved: await resolveNewVariant(item.handle, item) };
    });
    // 映射回原 variant_id 不是迁移结果。若继续进入 existingItem 分支，会把旧快照中的
    // quantity 再加到同一行自身，直接制造数量翻倍/跳大的问题；保留原行并提示用户处理。
    const toMigrate = resolvedList.filter(function (r) {
      return r.resolved && String(r.resolved.variantId) !== String(r.item.variant_id);
    });
    const anomalies = resolvedList.filter(function (r) {
      return !r.resolved || String(r.resolved.variantId) === String(r.item.variant_id);
    }).map(function (r) { return r.item; });
    if (!toMigrate.length) return { migratedCount: 0, anomalies: anomalies, migrations: [] };

    // 从这里开始真正要写购物车了：锁住 /cart/update.js，防止购物车迁移过程中系统里
    // 别的地方再次触发 update.js（读到的购物车快照过期，导致迁移失败/数据错乱）。
    // 锁定期间，非迁移自己发起的 update.js 请求会被 fetch-interceptor.js 直接丢弃
    // （见 CartUpdateGate 定义处的注释）。迁移完成（成功/失败都算数）后在 finally 里
    // unlock() 通知放开拦截
    if (window.CartUpdateGate) window.CartUpdateGate.lock();
    // 写之前先把这批计划存进补救队列，而不是等失败了才存：写入过程可能被整页跳转
    // （比如 fetch-interceptor.js 429 触发的验证跳转）或标签页关闭这类意外打断，一旦
    // 打断，"失败了才补录"这条路根本走不到，商品会直接消失且没有任何补救记录。这里
    // 跟 fetch-interceptor.js 的 handleRemovedItems 共用同一份补救队列
    // （window.SicotasPendingCartMigration），提前落盘、写入确认成功后再清掉，两条独立
    // 的迁移触发路径互不冲突（队列按 variant_id 去重）
    if (window.SicotasPendingCartMigration) {
      window.SicotasPendingCartMigration.enqueue(toMigrate.map(function (r) { return r.item; }));
    }
    try {
      // 两个近似重复的父商品拆分去重时，同一个 SKU 位置可能同时存在于两边（比如客户分别
      // 从"商品1234"和"商品2143"各加购过一次同一个 SKU），拆分后两条旧行会收敛到同一个
      // 新 variant 上。这种情况下不能各自独立发一条 add 请求（要么被 Shopify 按相同 id+
      // properties 合并、丢了其中一份优惠券信息，要么因为 properties 不同被当成两条不相关
      // 的行）——而是要合并成一条：数量相加，优惠券只延续"真正生成了这个新 variant 的那个
      // 旧 variant"（variant 级 metafield custom.source_variant_id，即 resolveNewVariant
      // 返回的 sourceVariantId）自己的记录，不是随便挑一条凑合用——不然会出现新商品保留的
      // 是 A 商品的身份，优惠券却错挂成 B 商品的情况
      const groupsByNewVariant = {};
      toMigrate.forEach(function (r) {
        var key = String(r.resolved.variantId);
        if (!groupsByNewVariant[key]) groupsByNewVariant[key] = [];
        groupsByNewVariant[key].push(r);
      });

      function pickCouponSource(members) {
        var bySource = members.find(function (r) {
          return r.resolved.sourceVariantId && String(r.resolved.sourceVariantId) === String(r.item.variant_id);
        });
        if (bySource) return bySource;
        // 分组数据里没记录 source_variant_id（老数据/边缘情况）时，没有可靠依据判断该延续
        // 哪一边，退化成"谁自带 properties.__klipC 就用谁"，至少不会凭空生造数据
        return members.find(function (r) { return r.item.properties && r.item.properties.__klipC; }) || members[0];
      }

      // "判断新 variant 是否已存在 → 决定新起一行还是并数量 → 写入"这整段必须在拿到
      // 这批 newVid 的锁之后才能做：这个函数和 fetch-interceptor.js 的
      // addMigratedVariants 是两条独立的迁移触发路径，两边完全可能同时处理收敛到同一个
      // 新 variant 的不同旧行（典型就是"两个近似重复商品拆分去重"场景）。之前这里在拿
      // 锁之前就读了一次 verifyCart 来判断存在性，两条路径各自读到"还不存在"就会各自
      // 新起一行、各自只按自己那份数量算，不会合并成一次总量写入——实测就是"迁移后总
      // 数量少了几件"这个丢件 bug 的根因。用 runMulti 把这批 newVid 全部锁住之后才现读
      // 最新购物车做判断，跟 fetch-interceptor.js 那边共用同一把锁（key 都是 variant
      // id），保证同一个新 variant 任意时刻只有一条路径能做这个决策+写入
      const newVidKeys = Object.keys(groupsByNewVariant);
      const writeResult = await window.SicotasCartLineLock.runMulti(newVidKeys, async function () {
        // 上一轮迁移可能已经把某个旧 variant 的数量加过了，只是紧接着的删旧行请求没送达
        // 就被刷新打断——这次重新扫到同一个旧行时，绝不能再把它的数量加第二遍，只需要
        // 补删旧行、补写优惠券。addedMarkers 记的是"旧 variant 数量已经加过"，跨刷新有效
        const addedMarkers = readAddedMarkers();

        // addedMarkers 只是历史记录，不是事实——它只在"加购成功"那一刻写入，之后购物车
        // 发生的任何事（别的写入把新商品也清掉了、用户自己删除了）它都不知情，标记会跟
        // 购物车真实状态脱节。脱节之后如果继续相信标记，就会跳过本该重新做的加购、只去
        // 删旧行，商品凭空消失——实测踩过这个坑。这里现读一份实时快照，只有标记里的旧
        // variant_id 对应的新变体**确实还在购物车里**，才真正当作"不用再加"，否则一律
        // 按"需要重新加购"处理，哪怕标记上写着已经加过
        let verifyCart = null;
        try {
          const verifyResponse = await fetchWithRetry('/cart.js', { credentials: 'same-origin', cache: 'no-store' });
          if (verifyResponse.ok) verifyCart = await verifyResponse.json();
        } catch (error) {
          // 拿不到就没法核实，保守起见当作标记全部失效，宁可多发一次加购请求（Shopify
          // 会自动合并同一 variant+properties 的行，不会重复），也不要凭旧标记漏加商品
        }
        // 这个新 variant 是不是已经在购物车里存在了——比如之前另一次独立的 migrateCart()
        // 调用已经把它加进来过，走的是另一个近似重复商品（同款同色同件数、但来自不同父
        // 商品）的优惠券。如果已经存在，不能再用 /cart/add.js 起一条新行：这批的 properties
        // 只要跟已有那行不完全一致（比如 __klipC 记的是另一份优惠券），Shopify 就会拆成
        // 两条独立的行——数量没法自动相加，画面上就是"同一个商品出现两行、优惠券还各算
        // 各的叠加显示"，实测就是这个问题的根因。这里改成直接把这批数量加到已有那一行上，
        // 不碰它已经生效的 properties/优惠券——谁先被迁移、谁的优惠券就先生效，之后同一个
        // 新 variant 再有别的旧行收敛过来，只并数量，不回头改已经定下来的优惠券归属（更
        // 彻底的"发现真正 source_variant_id 就纠正"方案更复杂、风险也更高，先用这个简单
        // 安全的版本）
        const existingItemsByVariant = {};
        (verifyCart && verifyCart.items || []).forEach(function (i) {
          if (!existingItemsByVariant[String(i.variant_id)]) existingItemsByVariant[String(i.variant_id)] = i;
        });

        const winnerByNewVariant = {};
        const addItems = [];
        const quantityBumps = [];
        const handledOldVariantIds = [];
        Object.keys(groupsByNewVariant).forEach(function (newVid) {
          var members = groupsByNewVariant[newVid];
          var existingItem = existingItemsByVariant[String(newVid)];
          var newVariantConfirmedInCart = !!existingItem;

          var pendingMembers = members.filter(function (r) {
            return !addedMarkers.has(String(r.item.variant_id)) || !newVariantConfirmedInCart;
          });
          if (!pendingMembers.length) return; // 这一组这次不用再加购，交给后面的删旧行/补优惠券逻辑处理

          var totalQuantity = pendingMembers.reduce(function (sum, r) { return sum + r.item.quantity; }, 0);

          if (existingItem) {
            // 已经有这个新 variant 的行了，只并数量，properties/优惠券保持原样不动。这里只记
            // 要加多少（addQuantity），不在这里就把 existingItem.quantity + totalQuantity 算成
            // 绝对值——这份 existingItem 来自上面 verifyCart 那次快照，到真正发写请求之间还有
            // 一段距离，如果用户这段时间里正好在改同一行的数量（cart-page-new.js 的
            // handleQuantityChange 也会走 SicotasCartLineLock），拿着这份快照算出的绝对值硬
            // 覆盖会把用户刚做的改动连带吞掉。实际的绝对值改到发写请求那一步、拿到锁之后再现读
            // 一次最新数量来算，见下面 quantityBumps 的写入逻辑
            quantityBumps.push({ key: existingItem.key, addQuantity: totalQuantity });
          } else {
            // 只有真正要新起一行时，才需要按 sourceVariantId 挑一个优惠券来源——已经存在
            // 的行不进这个分支，它的优惠券归属早在它自己那一轮就定下来了，这里不重新计算，
            // winnerByNewVariant 也不写入，这样下面 couponMigrations 的搬运逻辑不会误把
            // 这一轮"只是凑巧路过"的成员当成赢家，往购物车级 klipC 表里写错误记录
            var winner = pickCouponSource(members);
            winnerByNewVariant[newVid] = winner;
            var properties = winner.item.properties || {};
            if (window.KlipCouponResync && window.KlipCouponResync.rewriteItemProperties) {
              properties = window.KlipCouponResync.rewriteItemProperties(properties, {
                newVariantId: winner.resolved.variantId,
                newProductId: winner.resolved.productId
              });
            }
            addItems.push({ id: Number(newVid), quantity: totalQuantity, properties: properties });
          }
          pendingMembers.forEach(function (r) { handledOldVariantIds.push(r.item.variant_id); });
        });

        // 先批量加新/并量，成功后再批量删旧，避免任何失败路径丢商品
        let writesOk = true;

        if (addItems.length) {
          try {
            const addResponse = await fetchWithRetry('/cart/add.js', {
              method: 'POST',
              credentials: 'same-origin',
              // fetch-interceptor.js 现在对所有 add/update/change 写请求都会做迁移前后快照
              // 比对（不再只认"klip"字样），带上这个头让它识别出这是自己发起的写请求、跳过
              // 那套多余的检测——这批旧行本来就是这次迁移自己接下来要处理的
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Sicotas-Migration': '1' },
              body: JSON.stringify({ items: addItems })
            });
            const addResult = await addResponse.json();
            if (!addResponse.ok || addResult.status === 422) {
              throw new Error(addResult.description || addResult.message || 'add to cart failed');
            }
          } catch (error) {
            // 批量加购是整体成功/失败的（Shopify /cart/add.js 一个 item 失败就整批失败），
            // 失败时不做任何删除，全部标记异常，保证不丢商品
            console.error('[variant-migration] batch add failed:', error);
            writesOk = false;
          }
        }

        if (writesOk && quantityBumps.length) {
          // 这里的锁 key 是"已存在行的 key"，跟外层 runMulti 锁的"新 variant_id"是两套
          // 不同命名空间的 key，不会互相冲突：外层锁保证的是"判断/新起该新 variant 这
          // 一步"互斥，这里保证的是"往这一行已有数量上累加"跟用户自己同时点击 +/- 互斥
          const bumpResults = await Promise.all(quantityBumps.map(function (b) {
            return window.SicotasCartLineLock.run(b.key, async function () {
              try {
                const latestResponse = await fetchWithRetry('/cart.js', { credentials: 'same-origin', cache: 'no-store' });
                if (!latestResponse.ok) return false;
                const latestCart = await latestResponse.json();
                const latestItem = (latestCart.items || []).find(function (i) { return i.key === b.key; });
                if (!latestItem) return true; // 这一行已经不在购物车了（被别的操作删掉/替换了），没什么好并的
                const bumpResponse = await fetchWithRetry('/cart/update.js', {
                  method: 'POST',
                  credentials: 'same-origin',
                  headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Sicotas-Migration': '1' },
                  body: JSON.stringify({ updates: { [b.key]: latestItem.quantity + b.addQuantity } })
                });
                const bumpResult = await bumpResponse.json();
                if (!bumpResponse.ok || bumpResult.status === 422) {
                  throw new Error(bumpResult.description || bumpResult.message || 'merge quantity into existing line failed');
                }
                return true;
              } catch (error) {
                console.error('[variant-migration] merging quantity into an already-existing line failed:', error);
                return false;
              }
            });
          }));
          if (bumpResults.some(function (ok) { return !ok; })) {
            writesOk = false;
          }
        }

        return { writesOk: writesOk, handledOldVariantIds: handledOldVariantIds, winnerByNewVariant: winnerByNewVariant };
      });

      if (!writeResult.writesOk) {
        return { migratedCount: 0, anomalies: claimed, migrations: [] };
      }

      // 新行已经确认加成功/数量已经确认并上了，商品不会再丢，把预先存的记录清掉，
      // 避免下次页面加载被 processPendingQueue 当成"还没完成"重复处理一遍
      if (window.SicotasPendingCartMigration) {
        window.SicotasPendingCartMigration.remove(toMigrate.map(function (r) { return r.item.variant_id; }));
      }

      // 加购/并量一旦成功立刻落盘标记，就算紧接着的删旧行请求没送达就被刷新打断，
      // 下次重新扫到这批旧行也不会再重复处理一次
      markVariantsAdded(writeResult.handledOldVariantIds);

      const migrations = toMigrate.map(function (r) {
        return {
          oldVariantId: r.item.variant_id,
          oldProductId: r.item.product_id,
          oldKey: r.item.key,
          newVariantId: r.resolved.variantId,
          newProductId: r.resolved.productId
        };
      });

      // 不用最开始那份 cart 快照，现抓一份最新的，原因有两个：
      // 1. 优惠券记录：cart.attributes.klipC 是 Klip 自己异步写入/更新的，最开始那份快照
      //    是页面一加载就抓的，可能比 Klip 写完还要早，会漏看到刚写好的记录
      // 2. 判断旧行还在不在：Shopify 处理 /cart/add.js 时经常会顺带把引用了失效 variant
      //    的旧行自动清掉，等不到我们自己发删除请求。如果 updates 里带上一个已经不存在的
      //    key，Shopify 会把整个 /cart/update.js 请求判定失败（422），把本来合法的
      //    attributes.klipC 也一起搭进去——所以必须先知道"现在到底还剩哪些旧行"
      // cache: 'no-store' 是硬性要求：这次读取就是为了绕开上面两条已知的过期数据风险，
      // 如果浏览器/CDN 拿预取或预渲染缓存的旧响应糊弄过去，读到的还是没用的旧快照——
      // 实测踩过这个坑：读到的是迁移前的 8 件旧快照，而当时 Shopify 后端实际已经因为
      // 别的写入把购物车削到只剩 1 件，拿着这份假"还在"的旧快照去拼 updates，
      // 引用了根本不存在的 key，整个 /cart/update.js 请求 422，购物车状态更混乱
      let latestCart = cart;
      try {
        const latestResponse = await fetchWithRetry('/cart.js', { credentials: 'same-origin', cache: 'no-store' });
        if (latestResponse.ok) latestCart = await latestResponse.json();
      } catch (error) {
        // 抓不到最新的就退回用最开始那份，好过完全不搬
      }
      const stillExistingKeys = new Set((latestCart.items || []).map(function (i) { return i.key; }));

      const updates = {};
      migrations.forEach(function (m) {
        if (stillExistingKeys.has(m.oldKey)) updates[m.oldKey] = 0;
      });
      const updateBody = { updates: updates };

      // 购物车级 klipC 缓存表的搬运也只认每组里的"胜出方"，不能不管归属地把两条旧行的
      // 记录都搬过去——不然同一个新 variant 会同时收到两份互相矛盾的优惠券记录
      const couponMigrations = toMigrate
        .filter(function (r) { return writeResult.winnerByNewVariant[String(r.resolved.variantId)] === r; })
        .map(function (r) {
          return {
            oldVariantId: r.item.variant_id,
            oldProductId: r.item.product_id,
            newVariantId: r.resolved.variantId,
            newProductId: r.resolved.productId
          };
        });
      const klipCValue = window.KlipCouponResync && window.KlipCouponResync.computeMergedKlipC
        ? window.KlipCouponResync.computeMergedKlipC(latestCart, couponMigrations)
        : null;
      if (klipCValue) updateBody.attributes = { klipC: klipCValue };
      console.log('[variant-migration] cart-level klipC carry-over:', klipCValue ? 'found & attached' : 'no match found, not sent', couponMigrations);

      try {
        // 旧行可能已经被 Shopify 处理 add.js 时顺带清空，也可能压根没有优惠券可搬，
        // updates 和 attributes 都为空就没什么好发的了
        if (Object.keys(updates).length || updateBody.attributes) {
          const updateResponse = await fetchWithRetry('/cart/update.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              // 标记这是本迁移逻辑自己发起的写请求，不是 Klip 的前端脚本在写。
              // 请求体里带 attributes.klipC，字面上会命中 fetch-interceptor.js 里
              // "请求体含 klip 字样就当作 Klip 自己在写"的检测，导致被误当成一次新的
              // "Klip 静默清除商品"事件，多余地把这批本来就是我们自己在删的旧行又重新
              // 检测/搬运一遍——不会因此丢数据（claimMigration 已经挡住了），但会多打
              // 一轮请求、拖慢这次删除送达的时间，反而放大了"新商品已加、旧行还没删"
              // 这个真正有风险的窗口期。带上这个头，让 fetch-interceptor.js 识别出这
              // 不是 Klip 的写入，直接跳过那一整套多余的检测
              'X-Sicotas-Migration': '1'
            },
            body: JSON.stringify(updateBody)
          });
          if (updateResponse.ok) {
            // 旧行这次真的删掉了，标记可以清掉了，避免 sessionStorage 里无限堆积
            clearAddedMarkers(migrations.map(function (m) { return m.oldVariantId; }));
          }
        }
      } catch (error) {
        // 即使这次批量更新失败，新商品已经加成功了，不算迁移失败
        console.error('[variant-migration] batch update (remove old + coupon carry-over) failed:', error);
      }

      // 这一轮迁移干干净净地全部搬完了（没有一条卡在 anomalies 里），才有资格把"购物车
      // 已确认全部有效"的签名缓存写回去——现读一份最终快照（旧行删除、优惠券搬运都已
      // 落地之后的状态），而不是复用上面 latestCart（那份还带着即将被删掉的旧行）。
      // 只要还有 anomalies 没解决，就不写缓存，保证下次页面加载会继续重试，直到用户
      // 手动处理或数据修复为止
      let finalCart = null;
      if (!anomalies.length) {
        try {
          const finalResponse = await fetchWithRetry('/cart.js', { credentials: 'same-origin', cache: 'no-store' });
          if (finalResponse.ok) {
            finalCart = await finalResponse.json();
            markCartClean(finalCart);
            console.log('[variant-migration] migration fully clean, cached signature to skip future rescans');
          }
        } catch (error) {
          // 拿不到就不缓存，下次照常重新扫一遍，不影响正确性，只是少了这次的短路优化
        }
      }

      // 把这份已经读到的最终购物车数据带出去，调用方（cart-page-new.js/
      // cart-slide-drawer.js）刷新顶部购物车数量时可以直接复用，不用再单独现读一次
      // /cart.js——那次重复读取正是"迁移完成后顶部购物车数量不显示"的根因之一：命中
      // 429/网络抖动时会直接失败，导致更新数量的代码根本没机会执行
      return { migratedCount: migrations.length, anomalies: anomalies, migrations: migrations, cart: finalCart };
    } finally {
      if (window.CartUpdateGate) window.CartUpdateGate.unlock();
    }
  }

  window.VariantMigration = {
    getVariantValidity: getVariantValidity,
    isVariantValid: isVariantValid,
    resolveNewVariant: resolveNewVariant,
    claimMigration: claimMigration,
    migrateCart: migrateCart,
    markCartClean: markCartClean
  };
})();
